import { useEffect, useRef } from 'react';
import { Navigate, useNavigate, RouteObject, useLocation } from 'react-router-dom';

import { ProtectedRoute } from '@components/ProtectedRoute';
import { LoginPage } from '@auth_module/pages/LoginPage';
import { RegisterPage } from '@auth_module/pages/RegisterPage';
import { ForgotPasswordPage } from '@auth_module/pages/ForgotPasswordPage';
import { ResetPasswordPage } from '@auth_module/pages/ResetPasswordPage';

import { useAuthContext } from '@auth_module/context/AuthContext';
import { useAuthParams } from '@auth_module/hooks/useAuthParams';
import { RegisterDirectPage } from '@auth_module/pages/RegisterDirectPage';
import { VerifyEmailPage } from '@auth_module/pages/VerifyEmailPage';
import { VerifyPendingPage } from '@auth_module/pages/VerifyPendingPage';

function validateInviteToken(token: string | null | undefined) {
  if (!token) return false;

  return token.length > 0; // Validation logic here
}

export const _authRoutes = (): RouteObject[] => {
  const { auth, logout, setAuth } = useAuthContext();
  const navigate = useNavigate();
  const [{ invite_token: inviteToken, email: inviteEmail, adc_logout, cid }] = useAuthParams();
  const location = useLocation();
  const isLogoutTriggeredRef = useRef(false);

  async function handleLogout(cid?: string) {
    await logout();
    setAuth({ token: '', user: {} });
    setTimeout(() => {
      // if user was logged in but got on page with cid param, we need to logout and redirect to login page
      navigate(`/login${cid ? '?cid=' + cid : ''}`, { replace: true });
      window.location.reload();
    }, 300);
  }

  useEffect(() => {
    if (adc_logout) {
      handleLogout();
    }

    if (cid) {
      handleLogout(cid);
    }
  }, []);

  useEffect(() => {
    if (location.pathname === '/register-direct' && !isLogoutTriggeredRef.current && auth.token) {
      isLogoutTriggeredRef.current = true;

      logout().then(() => {
        setAuth({ token: '', user: {} });
      });
    }
  }, [location.pathname]);

  return [
    {
      path: 'login',
      element: (
        <ProtectedRoute redirectPath='/insights' isAllowed={!auth.token.length}>
          <LoginPage />
        </ProtectedRoute>
      ),
    },
    {
      path: 'reset-password',
      element: (
        <ProtectedRoute redirectPath='/insights' isAllowed={!auth.token.length}>
          <ResetPasswordPage />
        </ProtectedRoute>
      ),
    },
    {
      path: 'forgot-password',
      element: (
        <ProtectedRoute redirectPath='/insights' isAllowed={!auth.token.length}>
          <ForgotPasswordPage />
        </ProtectedRoute>
      ),
    },
    {
      path: 'register',
      element: (
        <ProtectedRoute
          redirectPath='login'
          isAllowed={validateInviteToken(inviteToken) && !!inviteEmail && !auth.user.id}>
          <RegisterPage />
        </ProtectedRoute>
      ),
    },
    {
      path: 'register-direct',
      element: (
        <ProtectedRoute redirectPath='/dashboard' isAllowed={true}>
          <RegisterDirectPage />
        </ProtectedRoute>
      ),
    },
    {
      path: 'email/verify',
      element: (
        <ProtectedRoute
          redirectPath='/'
          isAllowed={!!auth.token.length && !auth.user.email_verified}>
          <VerifyPendingPage />
        </ProtectedRoute>
      ),
    },
    {
      path: 'email/verify/*',
      element: (
        <ProtectedRoute
          redirectPath='/email/verify'
          isAllowed={!!auth.token.length && !auth.user.email_verified}>
          <VerifyEmailPage />
        </ProtectedRoute>
      ),
    },
  ];
};

export const _notFound = (): RouteObject[] => {
  return [
    {
      path: '*',
      element: (
        <ProtectedRoute redirectPath='login' isAllowed={false}>
          <Navigate replace to='login' />
        </ProtectedRoute>
      ),
    },
  ];
};
